import {
  CLINICAL_FORMS,
  EMR_FORMS,
  ageInYears,
  emrFormAppliesToPatient,
  isClinicalFormCode,
  resolveEmrForms,
  type ClinicalFormCode,
  type IpdOrderKind,
  type ResolvedEmrForm,
} from '@his/shared';
import { pickSection } from '@/components/clinical-shell/section-param';
import type { OrderPatient } from '../../opd/emr/orders/types';
import type { Admission } from '../admission/types';

export const IPD_EMR_HOME = 'overview';

export const IPD_EMR_CHART_SECTIONS = ['overview', 'vitals', 'emar', 'task-chart', 'nursing-notes'] as const;
export type IpdEmrChartSection = (typeof IPD_EMR_CHART_SECTIONS)[number];

export const IPD_EMR_FORM_HOSTS = [
  'progress-notes',
  'orders',
  'investigations',
  'procedures',
  'oxygen',
  'transfusion',
  'physiotherapy',
  'consults',
  'monitoring',
  'discharge-summary',
] as const;
export type IpdEmrFormHost = (typeof IPD_EMR_FORM_HOSTS)[number];

export type IpdEmrFormEntryId = IpdEmrFormHost | ClinicalFormCode;

export type IpdEmrSectionId = IpdEmrChartSection | IpdEmrFormEntryId;

export const ONE_KIND_HOSTS = {
  oxygen: 'oxygen',
  transfusion: 'blood_transfusion',
  physiotherapy: 'physiotherapy',
  consults: 'consult',
  monitoring: 'vitals_monitoring',
} as const satisfies Partial<Record<IpdEmrFormHost, IpdOrderKind>>;
export type IpdEmrOneKindHost = keyof typeof ONE_KIND_HOSTS;

const HOST_OF_FORM: Readonly<Record<string, IpdEmrFormHost>> = {
  progress_note: 'progress-notes',
  medication_order: 'orders',
  iv_fluid_order: 'orders',
  diet_order: 'orders',
  nursing_order: 'orders',
  lab_order: 'investigations',
  radiology_order: 'investigations',
  procedure_order: 'procedures',
  oxygen_order: 'oxygen',
  blood_transfusion_order: 'transfusion',
  physiotherapy_order: 'physiotherapy',
  consult_order: 'consults',
  vitals_monitoring_order: 'monitoring',
  discharge_summary: 'discharge-summary',
};

export interface IpdEmrFormEntry {
  id: IpdEmrFormEntryId;
  formKeys: string[];
  required: boolean;
}

export interface IpdEmrFormOptions {
  clinicalForms?: boolean;
}

export function isIpdClinicalForm(id: string): id is ClinicalFormCode {
  return isClinicalFormCode(id);
}

export function hostOfForm(formKey: string): IpdEmrFormHost | null {
  return HOST_OF_FORM[formKey] ?? null;
}

export function isOneKindHost(id: string): id is IpdEmrOneKindHost {
  return Object.prototype.hasOwnProperty.call(ONE_KIND_HOSTS, id);
}

export type IpdFormPatient = Pick<Admission['patient'], 'sex' | 'dob' | 'approximateAgeYears'>;

export function formsForPatient(forms: readonly ResolvedEmrForm[], patient: IpdFormPatient): ResolvedEmrForm[] {
  const facts = { sex: patient.sex, ageYears: ageInYears(patient.dob?.slice(0, 10) ?? null, patient.approximateAgeYears ?? null) };
  return forms.filter((form) => emrFormAppliesToPatient(form.key, facts));
}

export function defaultIpdForms(patient?: IpdFormPatient): ResolvedEmrForm[] {
  const forms = resolveEmrForms(EMR_FORMS, [], [], 'ipd');
  return patient ? formsForPatient(forms, patient) : forms;
}

export function ipdEmrFormEntries(forms: readonly ResolvedEmrForm[], hosts: ReadonlySet<IpdEmrFormHost>, options: IpdEmrFormOptions = {}): IpdEmrFormEntry[] {
  const entries = new Map<IpdEmrFormEntryId, IpdEmrFormEntry>();
  const ordered = [...forms].map((form, index) => ({ form, index })).sort((a, b) => a.form.sortOrder - b.form.sortOrder || a.index - b.index);
  for (const { form } of ordered) {
    if (!form.enabled) continue;
    if (form.kind === 'clinical_form' && isClinicalFormCode(form.key)) {
      if (options.clinicalForms && !entries.has(form.key)) entries.set(form.key, { id: form.key, formKeys: [form.key], required: form.required });
      continue;
    }
    const host = hostOfForm(form.key);
    if (!host || !hosts.has(host)) continue;
    const entry = entries.get(host);
    if (entry) {
      entry.formKeys.push(form.key);
      entry.required = entry.required || form.required;
    } else {
      entries.set(host, { id: host, formKeys: [form.key], required: form.required });
    }
  }
  return [...entries.values()];
}

export function ipdEmrSectionIds(chart: readonly IpdEmrChartSection[], entries: readonly IpdEmrFormEntry[]): IpdEmrSectionId[] {
  return [...chart, ...entries.map((entry) => entry.id)];
}

export function ipdEmrSectionFromParams(params: URLSearchParams, available: readonly IpdEmrSectionId[]): IpdEmrSectionId {
  return pickSection(params.get('section'), available, IPD_EMR_HOME) as IpdEmrSectionId;
}

export interface IpdEmrAccess {
  can: (...permissions: string[]) => boolean;
  hasModule: (module: 'lab' | 'radiology') => boolean;
}

export function ipdEmrChartSections(access: Pick<IpdEmrAccess, 'can'>): IpdEmrChartSection[] {
  return IPD_EMR_CHART_SECTIONS.filter((id) => {
    if (id === 'emar') return access.can('emar.read', 'emar.administer');
    if (id === 'task-chart') return access.can('emar.read', 'emar.administer', 'nursing.note.write');
    return true;
  });
}

export function ipdEmrHosts(access: IpdEmrAccess): Set<IpdEmrFormHost> {
  const hosts = new Set<IpdEmrFormHost>(['progress-notes', 'discharge-summary']);
  if (access.can('cpoe.write', 'emar.read', 'admission.read')) {
    hosts.add('orders');
    for (const host of Object.keys(ONE_KIND_HOSTS) as IpdEmrOneKindHost[]) hosts.add(host);
  }
  if ((access.hasModule('lab') || access.hasModule('radiology')) && access.can('admission.read', 'admission.manage', 'order.write')) hosts.add('investigations');
  if (access.can('order.write', 'admission.read')) hosts.add('procedures');
  return hosts;
}

export function ipdClinicalFormsVisible(access: Pick<IpdEmrAccess, 'can'>): boolean {
  return access.can('admission.read', 'encounter.read', 'encounter.read.own');
}

export function mayRecordAdmissionForm(code: ClinicalFormCode, access: Pick<IpdEmrAccess, 'can'> & { userId: string | null }, careTeam: readonly string[]): boolean {
  const onTeam = access.userId !== null && careTeam.includes(access.userId);
  if (access.can('encounter.write', 'cpoe.write') && (onTeam || access.can('encounter.cover'))) return true;
  return CLINICAL_FORMS[code].nurse && access.can('vitals.write', 'nursing.note.write');
}

export type InvestigationKind = 'lab' | 'radiology';

export function investigationKinds(formKeys: readonly string[], modules: Readonly<Record<InvestigationKind, boolean>>): InvestigationKind[] {
  const kinds: InvestigationKind[] = [];
  if (formKeys.includes('lab_order') && modules.lab) kinds.push('lab');
  if (formKeys.includes('radiology_order') && modules.radiology) kinds.push('radiology');
  return kinds;
}

export function ipdNewOrderPath(admissionId: string, returnTo: string, kind?: IpdOrderKind): string {
  const search = new URLSearchParams();
  if (kind) search.set('kind', kind);
  search.set('returnTo', returnTo);
  return `/ipd/admissions/${admissionId}/orders/new?${search.toString()}`;
}

export function orderPatientOf(patient: Pick<Admission['patient'], 'id' | 'sex' | 'dob' | 'approximateAgeYears'>): OrderPatient {
  return { id: patient.id, sex: patient.sex, dob: patient.dob, approximateAgeYears: patient.approximateAgeYears ?? null };
}
